import type { ApiClient } from './client';
import type { paths } from './generated/schema';

// The multipart-upload type machinery + FormData plumbing, centralised at the
// shared client boundary. openapi-fetch types a `multipart/form-data` body as a
// binary string and can't assemble a FormData itself, so every FE app used to
// carry a verbatim copy of this; they now bind it to their own `api` instance.

/** POST paths whose request body is a multipart file upload. */
export type UploadPath = {
	[P in keyof paths]: paths[P] extends {
		post: { requestBody: { content: { 'multipart/form-data': unknown } } };
	}
		? P
		: never;
}[keyof paths];

/** The path/query params for an upload endpoint (e.g. `{ path: { taskId } }`). */
export type UploadParams<P extends UploadPath> = paths[P]['post'] extends {
	parameters: infer Params;
}
	? Params
	: never;

/** The JSON body an upload endpoint returns (200 or 201). */
export type UploadData<P extends UploadPath> = paths[P]['post']['responses'] extends infer R
	? R extends { 200: { content: { 'application/json': infer D } } }
		? D
		: R extends { 201: { content: { 'application/json': infer D } } }
			? D
			: unknown
	: unknown;

/**
 * POST a single `file` as multipart/form-data through the typed client, so the
 * CSRF header + session cookie ride along automatically (a hand-rolled fetch
 * would miss the CSRF header and get a 403). The FormData build, the cast and the
 * pass-through serializer are centralised here; call sites stay cast-free and
 * keep the path's response type. Caller `unwrap`s the result.
 */
export function uploadFile<P extends UploadPath>(
	api: ApiClient,
	path: P,
	params: UploadParams<P>,
	file: File,
): Promise<{ data?: UploadData<P>; error?: unknown; response: Response }> {
	const form = new FormData();
	form.append('file', file);
	return api.POST(path, {
		params,
		body: { file },
		bodySerializer: () => form,
	} as never) as unknown as Promise<{ data?: UploadData<P>; error?: unknown; response: Response }>;
}
